import { Audience } from '@/models/audiences';
import { Trait } from '@/models/traits';
import { FormikProps } from 'formik';
import { FC, PropsWithChildren, RefObject } from 'react';
import { AudienceStepId } from '../constants';
import { AudienceConfirmation } from './AudienceConfirmation';
import { AudienceForm, AudienceFormModel } from './AudienceForm';
import { ExpressionContainer } from './expressions/ExpressionContainer';
import { AudienceSyncContainer, AudienceSyncFormModel } from './sync/AudienceSyncContainer';
import RouteConstants from '@/features/router/RouteConstants';
import { BreadcrumbItem, Breadcrumbs } from '@/lib/Breadcrumbs';
import { PageLayout } from '@/global/components';
import { AudiencePageHeader } from '@/features/audienceDetails/components/AudienceDetails_V2/AudiencePageHeader';
import { CreateStepper } from '@/lib/Steppers';
import { CreateStepArgs } from '@/lib/Steppers/CreateStepper/types';
import { useHistory } from 'react-router-dom';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'audiences:title',
    path: RouteConstants.audiences,
  },
];

type AudienceContentProps = {
  createFormRef: RefObject<FormikProps<AudienceFormModel>>;
  onCreateFormSubmit: (form: AudienceFormModel) => void;
  syncFormRef: RefObject<FormikProps<AudienceSyncFormModel>>;
  steps: CreateStepArgs<AudienceStepId>[];
  stepId: AudienceStepId;
  currentStepIndex: number;
  traits?: Trait[];
  audience?: Partial<Audience>;
  readOnly?: boolean;
} & PropsWithChildren;

export const AudienceContent: FC<AudienceContentProps> = ({
  createFormRef,
  onCreateFormSubmit,
  stepId,
  steps,
  currentStepIndex,
  syncFormRef,
  traits,
  audience,
  readOnly,
  children,
}) => {
  const history = useHistory();

  const handleViewAllAudiences = () => {
    history.push(RouteConstants.audiences);
  };

  const handleCreateNewAudience = () => {
    history.push(RouteConstants.campaigns);
  };

  const stepTemplate = () => {
    switch (stepId) {
      case 'nameAndMetadata':
        return <AudienceForm formRef={createFormRef} onSubmit={onCreateFormSubmit} traits={traits} audience={audience} readOnly={readOnly} />;
      case 'expression':
        return <ExpressionContainer />;
      case 'sync':
        return <AudienceSyncContainer formRef={syncFormRef} />;
      case 'confirmation':
        return (
          <AudienceConfirmation
            primaryButton={{
              text: 'ALL.createNewCampaign',
              onClick: handleCreateNewAudience,
            }}
            secondaryButton={{
              text: 'ALL.seeAllAudiences',
              onClick: handleViewAllAudiences,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <div className='flex h-full flex-col' data-testid='audienceEdit'>
        <div className='flex flex-1 flex-col'>
          <div className='shrink-0'>
            <Breadcrumbs items={breadcrumbs} className='mb-4' />
            <AudiencePageHeader title={'audience_edit:title'} subtitle={'audience_edit:subtitle'} hasButton={false} />
          </div>
          <div className='mt-8 flex flex-1 flex-col rounded-lg'>
            <div className='flex-1  bg-white-100'>
              <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />
              <div className='mt-6 rounded-lg'>{stepTemplate()}</div>
              {children}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
